import React, { useMemo, useState, useEffect } from 'react';
import Papa from 'papaparse';
import { supabase } from '@/integrations/supabase/client';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// Minimal set of fields we will map and send to the edge function
const TARGET_FIELDS = [
  'profession',
  'first_name',
  'last_name',
  'email',
  'phone',
  'license_number',
  'license_state',
  'experience_years',
  'specialties',
  'company_name',
  'company_website',
  'service_areas',
  'availability',
  'service_city',
  'service_zip_code',
  'website_url',
  'bio',
] as const;

type TargetField = typeof TARGET_FIELDS[number];

type Mapping = Record<TargetField, string | null>;

type ParsedRow = Record<string, any>;

type MappedRow = Partial<Record<TargetField, any>>;

function normalize(str: string) {
  return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

function autoMap(headers: string[]): Mapping {
  const mapping: Mapping = Object.fromEntries(TARGET_FIELDS.map((f) => [f, null])) as Mapping;
  const normalized = headers.map((h) => ({ raw: h, norm: normalize(h) }));

  const candidates: Record<TargetField, string[]> = {
    profession: ['profession', 'role', 'category', 'title'],
    first_name: ['first_name', 'firstname', 'first'],
    last_name: ['last_name', 'lastname', 'last', 'surname'],
    email: ['email', 'email_address'],
    phone: ['phone', 'phone_number', 'mobile', 'cell'],
    license_number: ['license_number', 'license', 'licensenumber'],
    license_state: ['license_state', 'state', 'licensestate'],
    experience_years: ['experience_years', 'experience', 'years_experience', 'years'],
    specialties: ['specialties', 'specialty', 'skills', 'areas_of_expertise'],
    company_name: ['company_name', 'company', 'organization', 'org'],
    company_website: ['company_website', 'company_site', 'company_url'],
    service_areas: ['service_areas', 'coverage_areas', 'areas_served', 'cities'],
    availability: ['availability', 'status'],
    service_city: ['service_city', 'city'],
    service_zip_code: ['service_zip_code', 'zip', 'zipcode', 'postal_code'],
    website_url: ['website_url', 'website', 'url', 'site'],
    bio: ['bio', 'about', 'description', 'summary'],
  };

  (Object.keys(candidates) as TargetField[]).forEach((field) => {
    const found = normalized.find((h) => candidates[field].includes(h.norm));
    if (found) mapping[field] = found.raw;
  });

  // Ensure required fields are present
  if (!mapping.first_name) mapping.first_name = normalized.find((h) => h.norm.includes('first'))?.raw || null;
  if (!mapping.last_name) mapping.last_name = normalized.find((h) => h.norm.includes('last') || h.norm.includes('surname'))?.raw || null;
  if (!mapping.email) mapping.email = normalized.find((h) => h.norm.includes('email'))?.raw || null;
  if (!mapping.profession) mapping.profession = normalized.find((h) => h.norm.includes('profession') || h.norm.includes('role'))?.raw || null;

  return mapping;
}

function applyMapping(row: ParsedRow, mapping: Mapping): MappedRow {
  const out: MappedRow = {};
  for (const key of TARGET_FIELDS) {
    const source = mapping[key];
    if (!source) continue;
    let value = row[source as string];
    if (value == null) continue;

    // Normalize array-like fields from comma/semicolon lists
    if (['specialties', 'service_areas'].includes(key) && typeof value === 'string') {
      value = value
        .split(/[,;\n]/)
        .map((s: string) => s.trim())
        .filter(Boolean);
    }

    out[key] = value;
  }
  return out;
}

interface ImportJob {
  id: string;
  filename: string;
  total_rows: number;
  processed_rows: number;
  successful_rows: number;
  failed_rows: number;
  status: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  // Map from actual DB columns
  original_filename?: string;
  successful_imports?: number;
  failed_imports?: number;
}

export default function AdminImportProfessionals() {
  const [fileName, setFileName] = useState<string>('');
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Mapping>(() => Object.fromEntries(TARGET_FIELDS.map((f) => [f, null])) as Mapping);
  const [approve, setApprove] = useState(true);
  const [loading, setLoading] = useState(false);
  const [recentJobs, setRecentJobs] = useState<ImportJob[]>([]);
  const [retryLoading, setRetryLoading] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<{
    jobId: string | null;
    totalRows: number;
    processedRows: number;
    successfulRows: number;
    failedRows: number;
    currentChunk: number;
    totalChunks: number;
  }>({
    jobId: null,
    totalRows: 0,
    processedRows: 0,
    successfulRows: 0,
    failedRows: 0,
    currentChunk: 0,
    totalChunks: 0,
  });

  const CHUNK_SIZE = 50; // Process 50 rows at a time

  const mappedPreview = useMemo(() => rows.slice(0, 5).map((r) => applyMapping(r, mapping)), [rows, mapping]);

  // Load recent jobs on mount
  useEffect(() => {
    loadRecentJobs();
  }, []);

  async function loadRecentJobs() {
    try {
      const { data, error } = await supabase
        .from('professional_import_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error loading recent jobs:', error);
        return;
      }

      // Map data to match interface and fix incorrect totals
      const mappedData = data?.map((job: any) => {
        // Calculate actual total from successful + failed imports
        const actualTotal = (job.successful_imports || 0) + (job.failed_imports || 0);
        // Use the calculated total if it's larger than the stored total_rows (fixes incorrect chunk-based totals)
        const correctedTotal = actualTotal > (job.total_rows || 0) ? actualTotal : (job.total_rows || 0);
        
        return {
          ...job,
          filename: job.original_filename || job.filename,
          successful_rows: job.successful_imports || job.successful_rows || 0,
          failed_rows: job.failed_imports || job.failed_rows || 0,
          total_rows: correctedTotal,
          updated_at: job.updated_at || job.created_at
        };
      }) || [];

      setRecentJobs(mappedData);
    } catch (err) {
      console.error('Error loading recent jobs:', err);
    }
  }

  function onFileSelected(file: File) {
    setFileName(file.name);
    setImportProgress({
      jobId: null,
      totalRows: 0,
      processedRows: 0,
      successfulRows: 0,
      failedRows: 0,
      currentChunk: 0,
      totalChunks: 0,
    });
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = (results.data as any[]).filter(Boolean);
        const hdrs = (results.meta.fields || []) as string[];
        setRows(data);
        setHeaders(hdrs);
        setMapping(autoMap(hdrs));
        toast.success(`Parsed ${data.length} rows`);
      },
      error: (err) => {
        toast.error(`CSV parse error: ${err.message}`);
      },
    });
  }

  async function handleImport() {
    if (!rows.length) {
      toast.error('Please upload a CSV first');
      return;
    }

    // Validate required fields present in mapping
    const missing: TargetField[] = [];
    ['profession', 'first_name', 'last_name', 'email'].forEach((f) => {
      if (!mapping[f as TargetField]) missing.push(f as TargetField);
    });
    if (missing.length) {
      toast.error(`Missing required mappings: ${missing.join(', ')}`);
      return;
    }

    const shaped = rows.map((r) => applyMapping(r, mapping));
    const chunks = [];
    for (let i = 0; i < shaped.length; i += CHUNK_SIZE) {
      chunks.push(shaped.slice(i, i + CHUNK_SIZE));
    }

    setLoading(true);
    setImportProgress({
      jobId: null,
      totalRows: shaped.length,
      processedRows: 0,
      successfulRows: 0,
      failedRows: 0,
      currentChunk: 0,
      totalChunks: chunks.length,
    });

    try {
      let jobId: string | null = null;
      let totalProcessed = 0;
      let totalSuccessful = 0;
      let totalFailed = 0;

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        const baseIndex = chunkIndex * CHUNK_SIZE;

        setImportProgress(prev => ({
          ...prev,
          currentChunk: chunkIndex + 1,
        }));

        const { data, error } = await supabase.functions.invoke('import-professionals', {
          body: {
            filename: fileName,
            approveImmediately: approve,
            rows: chunk,
            jobId: jobId, // null for first chunk, jobId for subsequent chunks
            baseIndex: baseIndex,
          },
        });

        if (error) {
          toast.error(`Chunk ${chunkIndex + 1} failed: ${error.message}`);
          break;
        }

        // Store job ID from first chunk
        if (!jobId) {
          jobId = data.job_id;
        }

        totalProcessed = data.total_processed || 0;
        totalSuccessful = data.total_successful || 0;
        totalFailed = data.total_failed || 0;

        setImportProgress(prev => ({
          ...prev,
          jobId: jobId,
          processedRows: totalProcessed,
          successfulRows: totalSuccessful,
          failedRows: totalFailed,
        }));

        // Add small delay to prevent overwhelming the server
        if (chunkIndex < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Finalize the job
      if (jobId) {
        await supabase.functions.invoke('import-professionals', {
          body: {
            jobId: jobId,
            finalize: true,
            rows: [], // Empty rows for finalize
          },
        });
      }

      toast.success(`Import complete: ${totalSuccessful} successful, ${totalFailed} failed`);
      
      // Reload recent jobs to show updated status
      loadRecentJobs();
    } catch (err) {
      toast.error('Import failed: ' + String(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleRetryJob(jobId: string, autoApprove: boolean = true) {
    setRetryLoading(jobId);
    
    try {
      const { data, error } = await supabase.functions.invoke('import-professionals', {
        body: {
          jobId: jobId,
          retryFailed: true,
          approveImmediately: autoApprove,
          excludeNoEmail: true, // Skip rows without email
        }
      });

      if (error) {
        toast.error('Retry failed: ' + error.message);
        return;
      }

      // Keep retrying until no more failures or max attempts
      let attempts = 0;
      const maxAttempts = 5;
      let currentData = data;
      
      while (currentData.failed_remaining > 0 && attempts < maxAttempts) {
        attempts++;
        
        const { data: nextData, error: nextError } = await supabase.functions.invoke('import-professionals', {
          body: {
            jobId: jobId,
            retryFailed: true,
            approveImmediately: autoApprove,
            excludeNoEmail: true,
          }
        });

        if (nextError) break;
        
        currentData = nextData;
        
        // Small delay between retries
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Finalize the job
      await supabase.functions.invoke('import-professionals', {
        body: {
          jobId: jobId,
          finalize: true,
        }
      });

      toast.success(
        `Retry complete: ${currentData.total_successful_retries || 0} additional imports${autoApprove ? ' (auto-approved)' : ''}`
      );
      
      // Reload recent jobs
      loadRecentJobs();
    } catch (err) {
      toast.error('Retry failed: ' + String(err));
    } finally {
      setRetryLoading(null);
    }
  }

  return (
    <>
      <SEO title="Import Professionals - Admin" description="Upload a CSV to import professionals" noindex={true} />
      <div className="p-6 space-y-6">
        {/* Recent Import Jobs */}
        {recentJobs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Import Jobs</CardTitle>
              <CardDescription>Previous imports with retry options</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentJobs.map((job) => (
                  <div key={job.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{job.filename}</span>
                        <Badge variant={job.status === 'completed' ? 'default' : job.status === 'processing' ? 'secondary' : 'outline'}>
                          {job.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {job.total_rows} total • {job.successful_rows} successful • {job.failed_rows} failed
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(job.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {job.failed_rows > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={retryLoading === job.id}
                          onClick={() => handleRetryJob(job.id, true)}
                        >
                          {retryLoading === job.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Retry Failed (Auto-approve)
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Professional Directory Import</CardTitle>
            <CardDescription>Upload a CSV and import professionals. Required columns: profession, first_name, last_name, email.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="space-y-2">
                <Label htmlFor="csv">CSV file</Label>
                <Input id="csv" type="file" accept=".csv,text/csv" onChange={(e) => e.target.files && onFileSelected(e.target.files[0])} />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <Switch id="approve" checked={approve} onCheckedChange={setApprove} />
                <Label htmlFor="approve">Approve immediately</Label>
              </div>
              <Button className="mt-6" disabled={loading || !rows.length} onClick={handleImport}>
                {loading ? 'Importing…' : 'Start Import'}
              </Button>
            </div>

            {loading && importProgress.totalRows > 0 && (
              <div className="space-y-3">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Processing chunk {importProgress.currentChunk} of {importProgress.totalChunks}</span>
                  <span>{importProgress.processedRows} / {importProgress.totalRows} rows</span>
                </div>
                <Progress 
                  value={(importProgress.processedRows / importProgress.totalRows) * 100} 
                  className="w-full" 
                />
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">{importProgress.successfulRows} successful</span>
                  <span className="text-red-600">{importProgress.failedRows} failed</span>
                </div>
                {importProgress.jobId && (
                  <p className="text-xs text-muted-foreground">Job ID: {importProgress.jobId}</p>
                )}
              </div>
            )}

            {headers.length > 0 && (
              <div className="border rounded-md p-4 space-y-3">
                <h3 className="font-medium">Column Mapping</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {TARGET_FIELDS.map((f) => (
                    <div key={f} className="flex items-center gap-3">
                      <Label className="w-48 capitalize">{f.replace(/_/g, ' ')}</Label>
                      <select
                        className="flex-1 rounded-md border bg-background px-3 py-2"
                        value={mapping[f] || ''}
                        onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value || null }))}
                      >
                        <option value="">,  not mapped , </option>
                        {headers.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mappedPreview.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium">Preview (first 5 rows)</h3>
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        {TARGET_FIELDS.map((f) => (
                          <th key={f} className="text-left p-2 border-b capitalize">{f.replace(/_/g, ' ')}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mappedPreview.map((r, idx) => (
                        <tr key={idx} className="border-b">
                          {TARGET_FIELDS.map((f) => (
                            <td key={f} className="p-2 align-top">
                              {Array.isArray(r[f]) ? (r[f] as any[]).join(', ') : (r[f] ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
